import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useUpdateHomeworkStatus from "../../hooks/api/homework/useUpdateHomeworkStatus";
import ConfirmDialog, { ConfirmDialogHandle } from "../ConfirmDialog";

type Props = {
  homeworkId: string;
  refetchHomework: () => void;
};

type FormValues = {
  message: string;
};

export default function RejectHomeworkButton({
  homeworkId,
  refetchHomework,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>();
  const { mutateAsync: rejectHomework, isPending: isRejectHomeworkLoading } =
    useUpdateHomeworkStatus(homeworkId);
  const confirmDialogRef = useRef<ConfirmDialogHandle>(null);

  const onRejectConfirmed = useCallback(() => {
    handleSubmit(({ message }) =>
      rejectHomework({ status: { status: "REJECTED", message } }).then(
        refetchHomework,
      ),
    )();
  }, [handleSubmit, refetchHomework, rejectHomework]);

  return (
    <>
      <Button
        onClick={() => confirmDialogRef.current?.open()}
        isLoading={isRejectHomeworkLoading}
      >
        {t("common.buttons.reject")}
      </Button>
      <ConfirmDialog
        ref={confirmDialogRef}
        title={t("homework_page.rejection")}
        message={t("homework_page.rejection_message")}
        onConfirm={onRejectConfirmed}
        isLoading={isRejectHomeworkLoading}
        bodyContent={
          <form>
            <FormControl flexGrow={1} mt={4}>
              <FormLabel hidden htmlFor="message">
                {t("forms.message")}
              </FormLabel>
              <Textarea
                {...register("message", {
                  required: t("forms.validation.required"),
                })}
                rows={4}
                id="message"
                placeholder={t("forms.message")}
              />
              <FormErrorMessage>
                {errors.message && errors.message.message}
              </FormErrorMessage>
            </FormControl>
          </form>
        }
      />
    </>
  );
}
